import {
  CustomToggleButtonGroup,
  OutlinedInput,
  Scrollbar,
  TableNoData,
} from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useJobTitleExpanderStore } from '../../store';
import { JobTitleCategoryToggleButtonType } from '../../types';
import { JobTitleItems } from '../job-title-items';
import { JobTitleListLoading } from '../job-title-list-loading';
import { getConfig } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { BrowseJobTitleListProps as Props } from './types';

export const BrowseJobTitleList = memo(({ className }: Props) => {
  const { data, isLoading, setSearchQuery } = useJobTitleExpanderStore();

  const [open, setOpen] = useState<string | undefined>(
    JobTitleCategoryToggleButtonType.AllTitles
  );
  const noData = !data?.titles;
  const isByCategories = useMemo(
    () => open === JobTitleCategoryToggleButtonType.ByCategories,
    [open]
  );
  const config = useMemo(() => getConfig(data), [data]);

  const handleChange = (value: string) => {
    setOpen(value);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (data?.titles) {
      if ('combined' in data.titles) {
        setOpen(JobTitleCategoryToggleButtonType.AllTitles);
      }
    }
  }, [data?.titles]);

  return (
    <Styled.Wrapper
      className={classnames('BrowseJobTitleList', className)}
      sx={SX.root}
    >
      <Typography variant="body3" fontWeight={600} color="grey.800">
        Browse Job Title
      </Typography>

      <Stack gap={0.5}>
        <CustomToggleButtonGroup
          onChange={handleChange}
          open={open}
          items={config}
        />
        <OutlinedInput
          placeholder="Search By Titles"
          size="small"
          startIcon="search"
          onChange={handleSearch}
        />
      </Stack>
      <Scrollbar sx={SX.scrollbar}>
        {data?.titles && !isLoading && (
          <JobTitleItems isByCategories={isByCategories} />
        )}

        {noData && !isLoading && (
          <Box sx={SX.noData}>
            <TableNoData
              isError={false}
              config={{
                title: 'No Generated Titles',
                emptyIcon: 'outline-auto-awesome',
              }}
            />
          </Box>
        )}
        {isLoading && <JobTitleListLoading />}
      </Scrollbar>
    </Styled.Wrapper>
  );
});

BrowseJobTitleList.displayName = 'BrowseJobTitleList';
